import React from 'react';
import { FiCheckCircle } from 'react-icons/fi';
import SectionHeader from '@components/common/SectionHeader';
import Card from '@components/common/Card';
import { PARTNERS } from '@utils/constants';

/**
 * PartnersSection Component
 * Displays funding partners and supporters
 */
const PartnersSection = () => {
  // Group partners by status
  const activePartners = PARTNERS.filter(p => p.status === 'Active');
  const completedPartners = PARTNERS.filter(p => p.status === 'Completed');

  return (
    <section className="section bg-bukawa-light">
      <div className="container-custom">
        {/* Section Header */}
        <SectionHeader
          subtitle="Our Partners"
          title="Supported by Leading Organizations"
          description="We are proud to collaborate with development partners who share our vision for agricultural transformation"
          className="mb-16"
        />

        {/* Active Partners */}
        <div className="mb-16">
          <h3 className="text-2xl font-heading font-semibold text-bukawa-primary mb-8 flex items-center gap-3">
            <span className="flex-shrink-0 w-2 h-2 bg-bukawa-secondary rounded-full animate-pulse" />
            Active Partnerships
          </h3>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activePartners.map((partner) => (
              <PartnerCard key={partner.id} partner={partner} />
            ))}
          </div>
        </div>

        {/* Completed Partners */}
        {completedPartners.length > 0 && (
          <div>
            <h3 className="text-2xl font-heading font-semibold text-bukawa-gray-700 mb-8 flex items-center gap-3">
              <FiCheckCircle className="h-6 w-6 text-bukawa-primary" />
              Past Partnerships
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              {completedPartners.map((partner) => (
                <PartnerCard key={partner.id} partner={partner} />
              ))}
            </div>
          </div>
        )}

        {/* Impact Summary */}
        <div className="mt-16">
          <Card className="bg-white border-2 border-bukawa-primary-100">
            <div className="grid md:grid-cols-3 gap-8 items-center">
              {/* Total Support */}
              <div className="text-center md:text-left">
                <div className="text-4xl font-bold text-bukawa-primary mb-2">
                  1.2B+
                </div>
                <div className="text-bukawa-gray-600">
                  Total Support (UGX)
                </div>
              </div>

              {/* Divider */}
              <div className="hidden md:block border-l border-bukawa-gray-200 h-20 mx-auto" />

              {/* Description */}
              <div className="col-span-2 text-center md:text-left">
                <p className="text-bukawa-gray-700 leading-relaxed">
                  Through strategic partnerships, we have secured significant
                  funding for maize processing, post-harvest handling, and asset
                  financing to better serve our farmer members.
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Partnership Call to Action */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col items-center gap-4 p-8 bg-white rounded-2xl shadow-soft">
            <h4 className="text-xl font-semibold text-bukawa-gray-900">
              Interested in Partnering with Us?
            </h4>
            <p className="text-bukawa-gray-600 max-w-md">
              We welcome partnerships that align with our mission of empowering
              farmers and improving agricultural value chains.
            </p>
            <a
              href="/contact"
              className="btn-primary"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * PartnerCard Component
 * Individual partner display card
 */
const PartnerCard = ({ partner }) => {
  const statusColors = {
    Active: 'bg-green-100 text-green-700 border-green-200',
    Completed: 'bg-blue-100 text-blue-700 border-blue-200',
    Terminated: 'bg-gray-100 text-gray-700 border-gray-200',
  };

  return (
    <Card hover className="h-full bg-white border border-bukawa-gray-200">
      {/* Partner Header */}
      <div className="flex items-start justify-between mb-4">
        <h4 className="text-lg font-semibold text-bukawa-gray-900 flex-1 leading-tight">
          {partner.name}
        </h4>
        <span
          className={`
            flex-shrink-0 ml-3 px-3 py-1 rounded-full text-xs font-medium border
            ${statusColors[partner.status] || statusColors.Completed}
          `}
        >
          {partner.status}
        </span>
      </div>

      {/* Contribution */}
      <div className="mb-4">
        <span className="text-sm text-bukawa-gray-600">Contribution:</span>
        <p className="text-base font-semibold text-bukawa-primary">
          {partner.contribution}
        </p>
      </div>

      {/* Project */}
      <div className="mb-4">
        <span className="text-sm text-bukawa-gray-600">Project:</span>
        <p className="text-sm text-bukawa-gray-800">
          {partner.project}
        </p>
      </div>

      {/* Period (if exists) */}
      {partner.period && (
        <div className="pt-4 border-t border-bukawa-gray-200">
          <span className="text-xs text-bukawa-gray-500">Period:</span>
          <p className="text-sm text-bukawa-gray-700">
            {partner.period}
          </p>
        </div>
      )}
    </Card>
  );
};

export default PartnersSection;